import {Edit} from "@mui/icons-material";
import {TabContext, TabPanel} from "@mui/lab";
import {Badge, Box, Tab, Tabs, useTheme} from "@mui/material";
import React, {useCallback, useContext, useEffect, useReducer, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Api from "../../../../../common/Api";
import chatService, {ChatDto} from "../../../../../common/apis/chat/ChatService";
import {DiagramDto} from "../../../../../common/apis/diagram/DiagramDto";
import {DiagramInfoDto} from "../../../../../common/apis/diagram/DiagramInfoDto";
import {ILabelDto} from "../../../../../common/apis/label/ILabelDto";
import DiagramPropertyDefinitionContext from "../../../../../common/DiagramPropertyDefinitionContext";
import AttachmentsPanel from "../../../../../components/attachment/AttachmentsPanel";
import Grid from "../../../../../components/dialogs/Grid";
import EditableCollectionsComponent from "../../../../../components/fields/EditableCollectionsComponent";
import {EditableLabelsComponent} from "../../../../../components/fields/label/EditableLabelsComponent";
import TextField from "../../../../../components/fields/textfield/TextField";
import DataGridContainer from "../../../../../components/grid/DataGridContainer";
import NotSucceededAlert from "../../../../../components/NotSucceededAlert";
import SectionActionBar from "../../../../../components/section/SectionActionBar";
import {SectionActionButton} from "../../../../../components/section/SectionActionButton";
import SectionHeader from "../../../../../components/section/SectionHeader";
import SucceededAlert from "../../../../../components/SucceededAlert";
import {AttachmentType} from "../../../../../store/common/Attachments";
import {PersistentStateId} from "../../../../../store/common/Grid";
import {getUiComponentsStateUpdateAction, UiComponentsStateType} from "../../../../../store/common/UiComponentsState";
import {getFilterRefetchAction} from "../../../../../store/diagrams/Diagrams";
import {_transl} from "../../../../../store/localization/TranslMessasge";
import {IApplicationState} from "../../../../../store/Store";
import ActivitiesGrid from "../../activity/ActivitiesGrid";
import {ActivityGridColDefFactory} from "../../activity/ActivityGridColDefFactory";
import {ActivityTranslationKey} from "../../activity/ActivityTranslationKey";
import ChatBox from "../../chat/detail/ChatBox";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import ElementDetailDialog from "../../elementdetail/ElementDetailDialog";
import {TABS_HEIGHT} from "../../elementdetail/MainPanel";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import PropertiesPanel, {PropertyApi} from "../../property/PropertiesPanel";
import Snackbar from "../../snackbar/Snackbar";
import {DiagramController} from "../controller/DiagramController";
import {DiagramTranslationKey} from "../DiagramTranslationKey";
import DiagramViewer from "../DiagramViewer";
import DiagramDetailHeader from "./DiagramDetailHeader";
import {DiagramPropertyWrapperApi} from "./DiagramPropertyWrapperApi";
import DiagramUpdateDialog from "./DiagramUpdateDialog";
import {FetchStatus, LeftPanelTabId, RightPanelTabId} from "./enums";
import {useStyles} from "./styles";


interface IState {
    diagram: {
        fetchStatus: FetchStatus,
        isSilentFetch: boolean,
        data?: DiagramDto,
    },
    alertSucceeded?: {
        text: string
    },
    alertNotSucceeded?: {
        text: string,
    },
}

const initialState: IState = {
    diagram: {
        fetchStatus: FetchStatus.IN_PROGRESS,
        isSilentFetch: false,
    },
}

enum ActionType {
    UPDATE_DIAGRAM = "UPDATE_DIAGRAM",
    UPDATE_ALERT_SUCCEEDED = "UPDATE_ALERT_SUCCEEDED",
    UPDATE_ALERT_NOT_SUCCEEDED = "UPDATE_ALERT_NOT_SUCCEEDED",
    HIDE_ALL_DIALOGS = "HIDE_ALL_DIALOGS",
}

type Action =
    { type: ActionType.UPDATE_DIAGRAM, payload: { fetchStatus: FetchStatus, isSilentFetch: boolean, data: DiagramDto | undefined } }
    |
    { type: ActionType.UPDATE_ALERT_SUCCEEDED, payload: { text: string } | undefined }
    |
    { type: ActionType.UPDATE_ALERT_NOT_SUCCEEDED, payload: { text: string } | undefined }
    |
    { type: ActionType.HIDE_ALL_DIALOGS };

function getUpdatingDiagramAction(isSilentFetch: boolean): Action {
    return {
        type: ActionType.UPDATE_DIAGRAM,
        payload: {fetchStatus: FetchStatus.IN_PROGRESS, isSilentFetch: isSilentFetch, data: undefined},
    }
}

function getDiagramUpdatedAction(fetchStatus: FetchStatus.SUCCEEDED | FetchStatus.FAILED, diagram?: DiagramDto): Action {
    return {
        type: ActionType.UPDATE_DIAGRAM,
        payload: {fetchStatus: fetchStatus, isSilentFetch: false, data: diagram},
    }
}

function getHideAllDialogsAction(): Action {
    return {
        type: ActionType.HIDE_ALL_DIALOGS
    }
}

function reducer(state: IState, action: Action): IState {
    switch (action.type) {
        case ActionType.UPDATE_DIAGRAM:
            const isInProgress = action.payload.fetchStatus === FetchStatus.IN_PROGRESS;
            let diagram = {...state.diagram};
            if (isInProgress) {
                diagram.isSilentFetch = action.payload.isSilentFetch;
                if (!action.payload.isSilentFetch) {
                    diagram.data = undefined;
                }
            } else {
                diagram = action.payload;
            }
            return {
                ...state,
                diagram: diagram,
            };
        case ActionType.UPDATE_ALERT_SUCCEEDED:
            return {
                ...state,
                alertSucceeded: action.payload,
            };
        case ActionType.UPDATE_ALERT_NOT_SUCCEEDED:
            return {
                ...state,
                alertNotSucceeded: action.payload,
            };
        case ActionType.HIDE_ALL_DIALOGS:
            return {
                ...state,
                alertSucceeded: undefined,
                alertNotSucceeded: undefined,
            }
        default:
            return state;
    }
}

interface DiagramDetailPanelProps {
    id: string;
    controller: DiagramController;
    diagramInfoDto?: DiagramInfoDto;
    hideDiagramPreviewTab?: boolean;
    hideShowAndEditIcons?: boolean;
    showHeader? : boolean;
    onDiagramUpdated?: (id: string) => void;
}

function fetchDiagramById(diagramId: string, dispatch: React.Dispatch<Action>, isSilent: boolean) {
    dispatch(getUpdatingDiagramAction(isSilent));
    Api.diagrams.getDiagramById(diagramId).subscribe({
        next: (response) => dispatch(getDiagramUpdatedAction(FetchStatus.SUCCEEDED, response.response as DiagramDto)),
        error: () => dispatch(getDiagramUpdatedAction(FetchStatus.FAILED)),
    })
}

function refetchFilter(reduxDispatch: any) {
    reduxDispatch(getFilterRefetchAction());
}

function onPropertyUpdate(diagramId: string,
                          dispatch: React.Dispatch<Action>,
                          reduxDispatch: any) {
    fetchDiagramById(diagramId, dispatch, true);
    refetchFilter(reduxDispatch);
}

export default function DiagramDetailPanel({id, controller, hideDiagramPreviewTab, hideShowAndEditIcons, showHeader, onDiagramUpdated, diagramInfoDto}: DiagramDetailPanelProps) {
    const theme = useTheme();
    const classes = useStyles(theme);

    const propertyDefinitionContext = useContext(DiagramPropertyDefinitionContext);

    const propertyClientApi: PropertyApi = React.useMemo(() => {
        return new DiagramPropertyWrapperApi(id, propertyDefinitionContext);
    }, [id, propertyDefinitionContext]);

    const [{
        diagram, alertSucceeded, alertNotSucceeded,
    }, dispatch
    ] = useReducer(reducer, initialState);

    const hideDiagram = hideDiagramPreviewTab ? hideDiagramPreviewTab : false;


    const reduxDispatch = useDispatch();
    const selectedLeftPanelTab = useSelector((state: IApplicationState) => state.uiComponentsState.DIAGRAM_DETAIL_LEFTPANEL_SELECTED_TAB as LeftPanelTabId || LeftPanelTabId.DETAIL);
    const selectedRightPanelTab = useSelector((state: IApplicationState) => hideDiagramPreviewTab ? RightPanelTabId.ATTRIBUTES : (state.uiComponentsState.DIAGRAM_DETAIL_RIGHTPANEL_SELECTED_TAB as RightPanelTabId || RightPanelTabId.PREVIEW));
    const labels = useSelector((state: IApplicationState) => state.pages.common.options.labels.resource);

    const viewpoints = useSelector((state: IApplicationState) => state.pages.common.options.viewpoints.resource);

    const [chats, setChats] = useState<ChatDto[]>([]);
    const [elementDetailDialogInfo, setElementDetailDialogInfo] = useState<string | undefined>();
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

    useEffect(() => {
        fetchDiagramById(id, dispatch, false);
    }, [id]);

    const fetchChats = useCallback(() => {
        chatService.searchChatsByFilter({diagramId: id})
            .then(chats => {
                setChats(chats);
            })
            .catch(() => Snackbar.error(_transl(ErrorTranslationKey.FAILED_TO_LOAD_DATA)));
    }, [id]);

    useEffect(() => {
        fetchChats();
    }, [fetchChats]);

    function onChatCommentEdited(chatId: number, commentId: number, message: string) {
        const copiedChats = [...chats];
        const chat = copiedChats.find((chat) => chat.id === chatId);
        if (chat) {
            const comment = chat.chatComments.find(comment => comment.id === commentId);
            if (comment) {
                comment.message = message;
            }
            setChats(copiedChats);
        }
        fetchChats();
    }

    const diagramFetchStatus = diagram.fetchStatus;
    const isDiagramSilentFetch = diagram.isSilentFetch;
    const diagramInfo = diagram.data?.diagramInfo;
    const diagramAcl = diagramInfo?.acl;
    const isReadOnly = controller.isDiagramReadOnly(diagramAcl);
    const [attachmentsCount, setAttachmentsCount] = useState<number>(diagramInfoDto?.attachmentsCount || 0);

    function updateSelectedLeftPanelTab(tabId: LeftPanelTabId) {
        reduxDispatch(getUiComponentsStateUpdateAction(UiComponentsStateType.DIAGRAM_DETAIL_LEFTPANEL_SELECTED_TAB, tabId));
    }

    function updateSelectedRightPanelTab(tabId: RightPanelTabId) {
        reduxDispatch(getUiComponentsStateUpdateAction(UiComponentsStateType.DIAGRAM_DETAIL_RIGHTPANEL_SELECTED_TAB, tabId));
    }

    return (
        <>
        <Box height={"100%"} display={"flex"} flexDirection={"column"}>
            {((diagramFetchStatus === FetchStatus.SUCCEEDED && diagramInfo) ||
                    (diagramFetchStatus === FetchStatus.IN_PROGRESS && isDiagramSilentFetch && diagramInfo)) &&
                <Grid container noStyle={true} spacing={1} minHeight={0}>
                    {showHeader &&
                        <Grid item xs={12}>
                            <DiagramDetailHeader
                                    diagram={diagramInfo}
                                    hideShowAndEditDiagramIcons={hideShowAndEditIcons}
                            />
                        </Grid>
                    }
                    <Grid item xs={12} md={6}>
                        <TabContext value={selectedLeftPanelTab}>
                            <Tabs
                                value={selectedLeftPanelTab}
                                onChange={(e, val) => updateSelectedLeftPanelTab(val)}
                                indicatorColor="primary"
                                textColor="primary"
                                style={{height: TABS_HEIGHT}}
                            >
                                <Tab value={LeftPanelTabId.DETAIL} className={classes.tabs}
                                     label={<span>{_transl(DiagramTranslationKey.DETAIL_TAB_DETAIL)}</span>}/>
                                <Tab
                                    className={classes.tabs}
                                    label={
                                        <Badge badgeContent={`${chats.length}`}
                                               color={"secondary"}>
                                            {_transl(DiagramTranslationKey.DETAIL_TAB_COMMENTS)}
                                        </Badge>
                                    }
                                    value={LeftPanelTabId.CHATS}
                                />
                                <Tab
                                    className={classes.tabs}
                                    label={
                                        <Badge badgeContent={`${attachmentsCount}`}
                                               color={"secondary"}>
                                            {_transl(DiagramTranslationKey.DETAIL_TAB_ATTACHMENTS)}
                                        </Badge>
                                    }
                                    value={LeftPanelTabId.ATTACHMENTS}
                                />
                            </Tabs>
                            <TabPanel value={LeftPanelTabId.DETAIL}>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <SectionHeader title={_transl(DiagramTranslationKey.DETAILS)}>
                                            <SectionActionBar>
                                                <SectionActionButton
                                                        icon={<Edit fontSize={"inherit"}/>}
                                                        tooltip={_transl(CommonTranslationKey.EDIT)}
                                                        onClick={() => setUpdateDialogOpen(true)}
                                                        visible={!isReadOnly}/>
                                            </SectionActionBar>
                                        </SectionHeader>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                                label={_transl(DiagramTranslationKey.DETAIL_NAME)}
                                                value={diagramInfo.name || ""}
                                                readOnly={true}/>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                                label={_transl(DiagramTranslationKey.DETAIL_IDENTIFIER)}
                                                value={id || ""}
                                                readOnly={true}/>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                                label={_transl(DiagramTranslationKey.DETAIL_VIEWPOINT)}
                                                value={viewpoints.find(vp => vp.identifier === diagramInfo.viewpoint)?.name ?? diagramInfo.viewpoint ?? ""}
                                                readOnly={true}/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                                label={_transl(DiagramTranslationKey.DETAIL_DESCRIPTION)}
                                                value={diagramInfo.description || ""}
                                                readOnly={true}
                                                multiline
                                                minRows={1}
                                                maxRows={5}/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SectionHeader title={_transl(DiagramTranslationKey.LABELS_AND_COLLECTIONS)}/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <EditableLabelsComponent textFieldId={"diagram-detail-basic-panel-labels"}
                                                                 publicLabelsAssigningPermitted={diagramInfo.acl.canUpdatePublicLabels}
                                                                 allLabels={labels}
                                                                 initiallyAssignedLabels={diagramInfo.labels}
                                                                 doUpdate={(labels: ILabelDto[]) => Api.diagrams.updateLabels(id, labels).toPromise()}
                                                                 onSuccessfulUpdate={(labels: ILabelDto[]) => {
                                                                     onPropertyUpdate(id, dispatch, reduxDispatch);
                                                                     onDiagramUpdated?.(id);
                                                                 }}/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <EditableCollectionsComponent
                                            assignedCollectionCodes={diagramInfo.collections.map(label => label.code)}
                                            doUpdate={(items) => Api.diagrams.updateCollections(id, items).toPromise()}
                                            onSuccessfulUpdate={(collections) => {
                                                onPropertyUpdate(id, dispatch, reduxDispatch);
                                                onDiagramUpdated?.(id);
                                            }}
                                            readonly={!diagramAcl?.canUpdateCollection}/>
                                    </Grid>
                                </Grid>
                            </TabPanel>
                            <TabPanel value={LeftPanelTabId.CHATS}>
                                <ChatBox chats={chats}
                                         canCreateChats={diagram.data?.diagramInfo.acl.canCreateChats ?? false}
                                         onChatsUpdated={fetchChats}
                                         onChatCommentEdited={onChatCommentEdited}
                                         openElementDetail={(id: string) => setElementDetailDialogInfo(id)}
                                         diagramId={id}/>
                                {elementDetailDialogInfo &&
                                    <ElementDetailDialog initialElementId={elementDetailDialogInfo}
                                                         opened={true}
                                                         onClosed={() => setElementDetailDialogInfo(undefined)}/>
                                }
                            </TabPanel>
                            <TabPanel value={LeftPanelTabId.ATTACHMENTS}>
                                <AttachmentsPanel resourceType={AttachmentType.DIAGRAMS}
                                                  resourceId={id}
                                                  canUploadAttachment={diagramAcl?.canCreateAttachment}
                                                  onAttachmentsUpdated={(attachmentCount: number) => setAttachmentsCount(attachmentCount)}/>
                            </TabPanel>
                        </TabContext>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TabContext value={selectedRightPanelTab}>
                            <Tabs
                                value={selectedRightPanelTab}
                                onChange={(e, val) => updateSelectedRightPanelTab(val)}
                                indicatorColor="primary"
                                textColor="primary"
                            >
                                {!hideDiagram && <Tab className={classes.tabs}
                                                      value={RightPanelTabId.PREVIEW}
                                                      label={
                                                          <span>{_transl(DiagramTranslationKey.DETAIL_TAB_PREVIEW)}</span>}/>
                                }

                                <Tab
                                    className={classes.tabs}
                                    value={RightPanelTabId.ATTRIBUTES}
                                    label={<span>{_transl(DiagramTranslationKey.DETAIL_TAB_ATTRIBUTES)}</span>}
                                />
                                <Tab
                                    className={classes.tabs}
                                    value={RightPanelTabId.ACTIVITIES}
                                    label={<span>{_transl(ActivityTranslationKey.ACTIVITY_TITLE)}</span>}
                                />
                            </Tabs>
                            {alertSucceeded &&
                                <SucceededAlert text={alertSucceeded.text}
                                                opened={true}
                                                onClose={() => dispatch(getHideAllDialogsAction())}
                                />
                            }
                            {alertNotSucceeded &&
                                <NotSucceededAlert text={alertNotSucceeded.text}
                                                   opened={true}
                                                   onClose={() => dispatch(getHideAllDialogsAction())}
                                />
                            }
                            {!hideDiagram && <TabPanel value={RightPanelTabId.PREVIEW}>
                                <div className={classes.preview}>
                                    <div className={classes.controlPageSegment}>
                                        <DiagramViewer diagramId={id}/>
                                    </div>
                                </div>
                            </TabPanel>}
                            <TabPanel value={RightPanelTabId.ATTRIBUTES}>
                                <PropertiesPanel
                                        canUpdateProperties={diagramInfoDto?.acl.canUpdate ?? false}
                                        propertyApi={propertyClientApi}
                                        persistentStateId={PersistentStateId.DIAGRAM_DETAIL_PAGE_ATTRIBUTES_GRID}
                                        resourceId={id || ""} />
                            </TabPanel>
                            <TabPanel value={RightPanelTabId.ACTIVITIES}>
                                <Box marginBottom={2}>
                                    <DataGridContainer>
                                        <ActivitiesGrid columns={ActivityGridColDefFactory.buildForDiagramDetail()} filter={{entityIdent: id, validFrom: null, validThru: null}}/>
                                    </DataGridContainer>
                                </Box>
                            </TabPanel>
                        </TabContext>
                    </Grid>
                </Grid>
            }
            {diagramFetchStatus === FetchStatus.FAILED &&
                <div>
                    {_transl(DiagramTranslationKey.DETAIL_LOADING_FAILED)}
                </div>
            }
            {diagramFetchStatus === FetchStatus.IN_PROGRESS && !isDiagramSilentFetch &&
                <div>
                    {_transl(DiagramTranslationKey.DETAIL_LOADING_IN_PROCESS)}
                </div>
            }
        </Box>

        {diagramInfo &&
            <DiagramUpdateDialog
                    open={updateDialogOpen}
                    diagramId={id}
                    diagramDetails={{
                        name: diagramInfo.name ?? '',
                        description: diagramInfo.description ?? null,
                        viewpoint: diagramInfo.viewpoint ?? null,
                    }}
                    onClose={() => setUpdateDialogOpen(false)}
                    onUpdated={() => {
                        onPropertyUpdate(id, dispatch, reduxDispatch);
                        onDiagramUpdated?.(id);
                    }}/>
        }
        </>
    );

}
